// src/pages/admin/ManagerAccords.jsx
import React, { useState, useEffect, useCallback } from 'react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import accordService from '../../services/accordService';
import { useToast } from '../../hooks/useToast';
import { extractApiList } from '../../utils/responseAdapters';
import Button from '../../components/common/Button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCircleCheck, faClock, faBan, faXmark, faHandshake, faCheck,
} from '@fortawesome/free-solid-svg-icons';

const STATUS_META = {
  pending:    { label: 'Pending',    color: '#f59e0b', icon: faClock       },
  active:     { label: 'Active',     color: '#10b981', icon: faCircleCheck },
  terminated: { label: 'Terminated', color: '#ef4444', icon: faBan         },
  expired:    { label: 'Expired',    color: '#6b7280', icon: faBan         },
  rejected:   { label: 'Rejected',   color: '#ef4444', icon: faXmark       },
  cancelled:  { label: 'Cancelled',  color: '#6b7280', icon: faXmark       },
};

const TABS = ['pending', 'active', 'history'];

function StatusBadge({ status }) {
  const meta = STATUS_META[status] || { label: status, color: '#6b7280', icon: faClock };
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 6,
      background: meta.color + '22', color: meta.color,
      padding: '3px 10px', borderRadius: 20, fontWeight: 600, fontSize: 13,
    }}>
      <FontAwesomeIcon icon={meta.icon} size="xs" />
      {meta.label}
    </span>
  );
}

function formatDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}

// ─── Accord Card ──────────────────────────────────────────────────────────────

function AccordCard({ accord, actions }) {
  return (
    <div className="application-card" style={{ marginBottom: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 8 }}>
        <div>
          <div style={{ fontWeight: 700, fontSize: 15 }}>
            {accord.investor_info?.name || accord.investor_id || 'Investor'}
          </div>
          <div style={{ fontSize: 13, color: '#6b7280' }}>{accord.investor_info?.email}</div>
        </div>
        <StatusBadge status={accord.status} />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginTop: 12, fontSize: 13 }}>
        <div><span style={{ color: '#9ca3af' }}>Created:</span> {formatDate(accord.created_at)}</div>
        <div><span style={{ color: '#9ca3af' }}>Expires:</span> {formatDate(accord.end_date || accord.expires_at)}</div>
        <div><span style={{ color: '#9ca3af' }}>Revenue Share:</span> {accord.revenue_share ?? '—'}%</div>
      </div>
      {accord.terms && <div style={{ fontSize: 13, color: '#555', marginTop: 8 }}>{accord.terms}</div>}
      {actions && <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 12 }}>{actions}</div>}
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function ManagerAccords() {
  const { showToast } = useToast();
  const [accords, setAccords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('pending');

  const fetchAccords = useCallback(async () => {
    setLoading(true);
    try {
      const res = await accordService.listManagerAccords();
      setAccords(extractApiList(res?.data));
    } catch {
      showToast('Failed to load accords', 500);
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => { fetchAccords(); }, [fetchAccords]);

  const handleAction = async (action, accordId, label) => {
    try {
      await action(accordId);
      showToast(`Accord ${label}`, 200);
      fetchAccords();
    } catch {
      showToast(`Failed to ${label.toLowerCase()} accord`, 500);
    }
  };

  const filtered = accords.filter((a) => {
    if (tab === 'pending') return a.status === 'pending' || a.status === 'signed';
    if (tab === 'active') return a.status === 'active';
    if (tab === 'history') return ['terminated', 'expired', 'rejected', 'cancelled'].includes(a.status);
    return false;
  });

  return (
    <DashboardLayout>
      <div style={{ padding: '24px 20px', maxWidth: 860 }}>
        <h2 style={{ marginTop: 0, marginBottom: 16 }}>
          <FontAwesomeIcon icon={faHandshake} style={{ marginRight: 10, color: '#2d8cf0' }} />
          Investor Accords
        </h2>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: 0, marginBottom: 24, borderBottom: '2px solid #e5e7eb' }}>
          {TABS.map((t) => (
            <button key={t} onClick={() => setTab(t)} style={{
              padding: '10px 20px', border: 'none', background: 'none', cursor: 'pointer',
              fontWeight: tab === t ? 700 : 400, fontSize: 14,
              color: tab === t ? '#2d8cf0' : '#6b7280',
              borderBottom: tab === t ? '2px solid #2d8cf0' : '2px solid transparent',
              marginBottom: -2, textTransform: 'capitalize',
            }}>
              {t}
            </button>
          ))}
        </div>

        {loading ? (
          <p style={{ color: '#aaa' }}>Loading…</p>
        ) : filtered.length === 0 ? (
          <div style={{
            padding: 30, background: '#f9fafb', borderRadius: 12,
            color: '#888', textAlign: 'center', fontSize: 14,
          }}>
            No {tab} accords found.
          </div>
        ) : (
          filtered.map((a) => {
            const id = a.accord_id || a.id || a._id;
            let actions = null;
            if (tab === 'pending') {
              actions = (
                <>
                  <Button variant="secondary" onClick={() => handleAction(accordService.approveAccord, id, 'approved')}>
                    <FontAwesomeIcon icon={faCheck} style={{ marginRight: 4, color: '#10b981' }} />
                    Approve
                  </Button>
                  <Button variant="secondary" onClick={() => handleAction(accordService.rejectAccord, id, 'rejected')}>
                    <FontAwesomeIcon icon={faXmark} style={{ marginRight: 4, color: '#ef4444' }} />
                    Reject
                  </Button>
                </>
              );
            } else if (tab === 'active') {
              actions = (
                <Button variant="secondary" onClick={() => handleAction(accordService.terminateManagerAccord, id, 'terminated')}>
                  <FontAwesomeIcon icon={faBan} style={{ marginRight: 4, color: '#ef4444' }} />
                  Terminate
                </Button>
              );
            }
            return <AccordCard key={id} accord={a} actions={actions} />;
          })
        )}
      </div>
    </DashboardLayout>
  );
}
